/**
 * SSN (US Social Security Number) validation.
 *
 * Mirrors the format + range rules enforced by
 * `backend-tms/src/regulations/countries/usa/usa-driver.regulations.ts`
 * so the client can surface a precise reason before the request is sent
 * (instead of catching the generic 400 toast — see B-112).
 *
 * Accepted formats:
 *   - "XXX-XX-XXXX" (with hyphens)
 *   - "XXXXXXXXX"   (digits only) — convenience, normalized before checks
 *
 * Invalid ranges (per SSA):
 *   - Area (first 3) is 000, 666, or 9XX
 *   - Group (middle 2) is 00
 *   - Serial (last 4) is 0000
 */

const SSN_FORMATTED_REGEX = /^\d{3}-\d{2}-\d{4}$/;
const SSN_DIGITS_REGEX = /^\d{9}$/;

export type SsnValidationCode = 'INVALID_FORMAT' | 'INVALID_RANGE';

export interface SsnValidationResult {
  valid: boolean;
  code?: SsnValidationCode;
}

/**
 * Validate an SSN string. Returns `{ valid: true }` on success or
 * `{ valid: false, code }` with a discriminator so callers can localize
 * the message.
 */
export function validateSsn(raw: string): SsnValidationResult {
  if (!SSN_FORMATTED_REGEX.test(raw) && !SSN_DIGITS_REGEX.test(raw)) {
    return { valid: false, code: 'INVALID_FORMAT' };
  }

  const digits = raw.replace(/-/g, '');
  const area = digits.substring(0, 3);
  const group = digits.substring(3, 5);
  const serial = digits.substring(5, 9);

  if (area === '000' || area === '666' || area.startsWith('9')) {
    return { valid: false, code: 'INVALID_RANGE' };
  }
  if (group === '00' || serial === '0000') {
    return { valid: false, code: 'INVALID_RANGE' };
  }

  return { valid: true };
}
